/**
 * AoResultNotifier — Juicy notification on AO resolution (05/02/2026, Q1 engagement).
 *
 * Watches `state.lastAoResultEvent` and reacts when a new event lands :
 *   • Win   → success toast + bell sound + premium modal (national tier) with Sora 2 clip
 *   • Loss  → muted toast + low sad note
 *   • Counter → neutral toast + soft chime
 *
 * Sounds are generated on-the-fly via Web Audio API (no asset shipping).
 * Sora 2 clips live in `/public/ao-clips/{outcome}-{n}.mp4` and are picked
 * at random per resolution; if the asset is missing, the modal falls back
 * to a static animated badge.
 *
 * Sound toggle : `state.aoSoundEnabled` (default false to respect users
 * who play during a meeting). Toggle from Settings page.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from '../ui/sonner';
import { Trophy, X, Sparkles, Crown } from 'lucide-react';
import useGameStore from '../../store/gameStore';

// Number of pre-generated clips per outcome bucket (see scripts/generate-ao-clips.py).
// 05/02/2026 — 6 clips initialement générés via Sora 2 (budget épuisé sur la
// série complète de 10 — 4 prompts bloqués par modération + insufficient balance).
// Si plus de clips sont ajoutés plus tard, incrémenter ces compteurs.
const CLIP_COUNTS = { won: 3, lost: 1, counter: 2 };

function pickClipPath(outcome) {
  const max = CLIP_COUNTS[outcome] || 0;
  if (!max) return null;
  const idx = 1 + Math.floor(Math.random() * max);
  return `/ao-clips/${outcome}-${idx}.mp4`;
}

/**
 * Play a quick tonal pattern via Web Audio API. No asset, no network.
 */
function playPattern(pattern) {
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return;
    const ctx = new Ctor();
    const now = ctx.currentTime;
    pattern.forEach(({ freq, start, dur, gain = 0.18 }) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      g.gain.setValueAtTime(0, now + start);
      g.gain.linearRampToValueAtTime(gain, now + start + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(g).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.05);
    });
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 1500);
  } catch (_e) { /* mute if unsupported */ }
}

function playOutcomeSound(outcome) {
  if (outcome === 'won') {
    // Triumphant 3-note chime
    playPattern([
      { freq: 660, start: 0,    dur: 0.18 },
      { freq: 880, start: 0.10, dur: 0.20 },
      { freq: 1320, start: 0.22, dur: 0.40, gain: 0.22 },
    ]);
  } else if (outcome === 'counter') {
    // Neutral 2-note ping
    playPattern([
      { freq: 520, start: 0,    dur: 0.15 },
      { freq: 780, start: 0.12, dur: 0.30 },
    ]);
  } else {
    // Lost — descending sad note
    playPattern([
      { freq: 440, start: 0,    dur: 0.20, gain: 0.14 },
      { freq: 330, start: 0.18, dur: 0.40, gain: 0.12 },
    ]);
  }
}

export default function AoResultNotifier() {
  const evt = useGameStore(s => s.lastAoResultEvent);
  const consume = useGameStore(s => s.consumeLastAoResultEvent);
  const soundEnabled = useGameStore(s => s.aoSoundEnabled);
  const [premiumModal, setPremiumModal] = useState(null);

  const handleEvent = useCallback((event) => {
    if (!event) return;
    // 05/02/2026 — Q1 engagement : "premium" modal triggers for big-stake
    // AO outcomes (national tier always ; regional only on wins). Counter
    // and lost on small tiers stay a toast-only notification.
    const isWon = event.outcome === 'won';
    // 08/02/2026 — Q2 user prod : tous les AO loupés ET gagnés déclenchent
    // désormais la modale premium (pas seulement national/regional won).
    // Le user veut des popups dédiés sur les événements business clés.
    const showModal = true;
    const labelByOutcome = {
      won: `🏆 AO gagné chez ${event.clientName} !`,
      counter: `↩️ Contre-proposition reçue chez ${event.clientName}`,
      lost: `❌ AO perdu chez ${event.clientName}`,
    };
    const toneByOutcome = {
      won: 'success',
      counter: 'info',
      lost: 'error',
    };

    // 1) Toast
    const fn = toast[toneByOutcome[event.outcome]] || toast.message;
    try {
      fn(labelByOutcome[event.outcome] || 'Résultat AO', {
        description: showModal ? `Tier ${event.tier} — détails dans la vitrine` : undefined,
        duration: showModal ? 8000 : 5000,
        id: `ao-${event.tenderId}-${event.resolvedOnDay}`,
      });
    } catch { /* sonner not mounted yet */ }

    // 2) Sound (opt-in)
    if (soundEnabled) playOutcomeSound(event.outcome);

    // 3) Premium modal with Sora 2 clip for big-stake outcomes.
    if (showModal) {
      const clipSrc = pickClipPath(event.outcome);
      setPremiumModal({
        clipSrc,
        outcome: event.outcome,
        tier: event.tier,
        clientName: event.clientName,
        tenderId: event.tenderId,
      });
    }

    consume();
  }, [consume, soundEnabled]);

  useEffect(() => {
    if (!evt) return;
    handleEvent(evt);
  }, [evt, handleEvent]);

  return (
    <AnimatePresence>
      {premiumModal && (
        <motion.div
          key="ao-premium-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setPremiumModal(null)}
          data-testid="ao-premium-modal"
        >
          <motion.div
            initial={{ scale: 0.85, y: 30 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 240, damping: 22 }}
            className={`relative w-full max-w-md rounded-2xl border-2 p-5 shadow-2xl ${
              premiumModal.outcome === 'won'
                ? 'border-amber-400/60 bg-gradient-to-br from-amber-900/40 via-ink-900 to-amber-950/30 shadow-[0_0_60px_-10px_rgba(251,191,36,0.45)]'
                : premiumModal.outcome === 'counter'
                  ? 'border-sky-400/60 bg-gradient-to-br from-sky-900/40 via-ink-900 to-sky-950/30 shadow-[0_0_50px_-10px_rgba(56,189,248,0.4)]'
                  : 'border-slate-400/40 bg-gradient-to-br from-slate-800/60 via-ink-900 to-slate-900/30'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPremiumModal(null)}
              className="absolute top-2 right-2 p-1.5 rounded hover:bg-white/10 text-white/60 hover:text-white"
              data-testid="ao-premium-modal-close"
              aria-label="Fermer"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-2 mb-3">
              {premiumModal.outcome === 'won'
                ? <Crown className="w-5 h-5 text-amber-300" />
                : premiumModal.outcome === 'counter'
                  ? <Sparkles className="w-5 h-5 text-sky-300" />
                  : <X className="w-5 h-5 text-slate-400" />}
              <h3 className={`text-xs uppercase tracking-widest font-mono font-bold ${
                premiumModal.outcome === 'won' ? 'text-amber-300'
                  : premiumModal.outcome === 'counter' ? 'text-sky-300'
                    : 'text-slate-400'
              }`}>
                {premiumModal.outcome === 'won' && `AO ${premiumModal.tier === 'national' ? 'National' : 'Régional'} remporté`/* i18n-keep — narrative FR */}
                {premiumModal.outcome === 'counter' && `Contre-proposition ${premiumModal.tier}`/* i18n-keep */}
                {premiumModal.outcome === 'lost' && `AO ${premiumModal.tier} perdu`/* i18n-keep */}
              </h3>
            </div>

            <p className={`text-lg font-display font-bold mb-3 ${
              premiumModal.outcome === 'won' ? 'text-amber-100'
                : premiumModal.outcome === 'counter' ? 'text-sky-100'
                  : 'text-slate-200'
            }`}>
              {premiumModal.clientName/* i18n-keep — client proper noun */}
            </p>

            {premiumModal.clipSrc && (
              <ClipPlayer
                src={premiumModal.clipSrc}
                fallback={<TrophyFallback outcome={premiumModal.outcome} />}
              />
            )}

            <div className="mt-3 flex items-center gap-2 text-xs">
              {premiumModal.outcome === 'won' && (
                <>
                  <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                  <span className="text-amber-200/80">Bravo ! Le contrat est dans la boîte « disponible » pour staffing.</span>{/* i18n-keep — narrative FR */}
                </>
              )}
              {premiumModal.outcome === 'counter' && (
                <>
                  <Sparkles className="w-3.5 h-3.5 text-sky-300" />
                  <span className="text-sky-200/80">Le client propose un prix revu — votre offre attend votre décision.</span>{/* i18n-keep */}
                </>
              )}
              {premiumModal.outcome === 'lost' && (
                <span className="text-slate-400/80 italic">Pas cette fois — le client a retenu un autre prestataire. La prochaine sera la bonne.</span>/* i18n-keep */
              )}
            </div>

            <button
              type="button"
              onClick={() => setPremiumModal(null)}
              className={`mt-4 w-full py-2 rounded text-sm font-semibold transition-colors ${
                premiumModal.outcome === 'won'
                  ? 'bg-amber-600/30 border border-amber-500/50 text-amber-100 hover:bg-amber-600/40'
                  : premiumModal.outcome === 'counter'
                    ? 'bg-sky-600/30 border border-sky-500/50 text-sky-100 hover:bg-sky-600/40'
                    : 'bg-slate-700/40 border border-slate-500/40 text-slate-200 hover:bg-slate-700/50'
              }`}
              data-testid="ao-premium-modal-ack"
            >
              Continuer{/* i18n-keep */}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ClipPlayer({ src, fallback }) {
  const [broken, setBroken] = useState(false);
  if (broken) return fallback;
  return (
    <div className="relative rounded-lg overflow-hidden border border-amber-500/30 bg-black aspect-video">
      <video
        src={src}
        autoPlay
        muted
        loop
        playsInline
        className="w-full h-full object-cover"
        onError={() => setBroken(true)}
        data-testid="ao-premium-clip"
      />
      <div className="absolute inset-0 ring-1 ring-amber-400/20 pointer-events-none" />
    </div>
  );
}

function TrophyFallback({ outcome }) {
  const isWon = outcome === 'won';
  return (
    <motion.div
      initial={{ scale: 0.7, rotate: -10 }}
      animate={{ scale: 1, rotate: 0 }}
      transition={{ type: 'spring', stiffness: 200, damping: 14 }}
      className={`aspect-video rounded-lg border flex items-center justify-center ${
        isWon
          ? 'border-amber-500/30 bg-gradient-to-br from-amber-700/30 to-amber-900/20'
          : outcome === 'counter'
            ? 'border-sky-500/30 bg-gradient-to-br from-sky-700/30 to-sky-900/20'
            : 'border-slate-500/30 bg-gradient-to-br from-slate-700/30 to-slate-900/20'
      }`}
      data-testid="ao-premium-clip-fallback"
    >
      <Trophy
        className={`w-16 h-16 ${
          isWon ? 'text-amber-300 drop-shadow-[0_0_18px_rgba(251,191,36,0.7)]'
            : outcome === 'counter' ? 'text-sky-300' : 'text-slate-500'
        }`}
      />
    </motion.div>
  );
}
